import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useTabStore from "../stores/useTabStore";
import TabSystem from "../layout-components/TabSystem";
import DemoBanner from "@/ui-components/DemoBanner";
import { getWidgetById } from "@/utils/widgets";
import { trackFeatureUsage } from "@/utils/gtm";

let sessionOpenTracked = false;

const DashboardBuilder = () => {
  const { authenticated } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const activeTabId = useTabStore((state) => state.activeTabId);
  const tabsLoaded = useTabStore((state) => state.tabsLoaded);
  const initialTabApplied = useRef(false);

  // Unauth users have tabs immediately; auth users must wait for server response
  const tabsReady = !authenticated || tabsLoaded;

  // Once tabs are ready, apply ?tab= from URL if valid (one-time)
  useEffect(() => {
    if (!tabsReady || initialTabApplied.current) return;
    initialTabApplied.current = true;

    const tabParam = new URLSearchParams(window.location.search).get("tab");
    if (!tabParam) return;

    const { tabs, activeTabId, switchTab } = useTabStore.getState();
    if (tabParam !== activeTabId && tabs.some((t) => t.id === tabParam)) {
      switchTab(tabParam);
    }
  }, [tabsReady]);

  // When activeTabId changes, sync it to the URL
  useEffect(() => {
    if (!tabsReady) return;

    setSearchParams({ tab: activeTabId }, { replace: true });
  }, [activeTabId, tabsReady, setSearchParams]);

  // Fire one GA4 event per open widget on initial dashboard load
  useEffect(() => {
    if (!tabsReady || sessionOpenTracked) return;
    sessionOpenTracked = true;

    const { tabs, activeTabId } = useTabStore.getState();
    const activeTab = tabs.find((t) => t.id === activeTabId);
    if (!activeTab) return;

    for (const compositeId of activeTab.enabledWidgets) {
      const [widgetId] = compositeId.split("-");
      const widget = getWidgetById(widgetId);
      if (!widget) continue;
      trackFeatureUsage({
        feature_name: "widget_interaction",
        feature_action: "session_open",
        feature_detail: widget.name,
      });
    }
  }, [tabsReady]);

  if (!tabsReady) {
    return (
      <div className="animate-pulse flex h-screen items-center justify-center text-center text-gray-300">
        Loading dashboard...
      </div>
    );
  }

  return (
    <>
      {!authenticated && <DemoBanner />}
      <TabSystem />
    </>
  );
};

export default DashboardBuilder;
